import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApplicationVerifier, Auth, getMultiFactorResolver, IdTokenResult, multiFactor, onAuthStateChanged, ParsedToken, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, User } from '@angular/fire/auth';
import { LocalStorageCacheService } from './local-storage-cache.service';
import { Router } from '@angular/router';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private inactivityTimeout: any;
  private userActivitySubscription: Subscription = new Subscription();

  private loggedIn = false;
  userInfo:any = {};
  userClaims:any = {};
  private userSubject = new BehaviorSubject<User | null>(null);
  private idTokenSubject = new BehaviorSubject<IdTokenResult | null>(null);
  user$ = this.userSubject.asObservable();
  idToken$ = this.idTokenSubject.asObservable();

  recaptchaVerifier: ApplicationVerifier | RecaptchaVerifier | any;
  onOTPPage = false;
  isLoggedInAfterMFA = false;
  
  constructor(private router: Router, private http: HttpClient, private auth: Auth, private localStorageService: LocalStorageCacheService, private dataService: DataService) {
    this.listenForUserActivity();
    this.loggedIn = !!localStorage.getItem('access_token');
    this.auth.onAuthStateChanged( async user => { 
      if (user) {
        this.userSubject.next(user);
        const idToken = (await user.getIdTokenResult());
        this.userClaims = idToken.claims;
        this.idTokenSubject.next(idToken);
      }
    });
    this.idToken$.subscribe(async idToken => {
      this.isLoggedInAfterMFA = false;
      if (idToken && idToken.signInSecondFactor) {
        this.isLoggedInAfterMFA = true;
      }
    });
  }

  activateAction(userId: string, payload: { action: string; }) : Observable<any>  {
    const url = `${environment.BASE_URL}/users/${userId}/user-activate`;
    return this.http.put(url, payload).pipe(
      map((response:any) => {
        return true;
      }),
      catchError(() => {
        this.loggedIn = false;
        return of(false);
      })
    );
  }

  resetMfaForUser(userId: string) : Observable<any>  {
    const url = `${environment.BASE_URL}/auth/reset-mfa/${userId}`;
    return this.http.post(url, '').pipe(
      map((response:any) => {
        return true;
      }),
      catchError(() => {
        this.loggedIn = false;
        return of(false);
      })
    );
  }

  login(payload: any): Observable<any> {
    const url = `${environment.BASE_URL}/auth/login`;
    return this.http.post(url, payload).pipe(
      map((response:any) => {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        localStorage.setItem('session_id', response.session_id);
        this.loggedIn = true;
        return true;
      }),
      catchError(() => {
        this.loggedIn = false;
        return of(false);
      })
    );
  }

  update(userId: string, payload: any) : Observable<any> {
    const url = `${environment.BASE_URL}/users/${userId}/set-claims`;
    return this.http.put(url, payload).pipe(
      map((response:any) => {
        return true;
      }),
      catchError(() => {
        this.loggedIn = false;
        return of(false);
      })
    );
  }

  setCustomClaims(userId: string, payload: any) {
    const url = `${environment.BASE_URL}/auth/setCustomClaims/${userId}`;
    return this.http.put(url, payload).pipe(
      map((response:any) => {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        localStorage.setItem('session_id', response.session_id);
        this.loggedIn = true;
        return true;
      }),
      catchError(() => {
        this.loggedIn = false;
        return of(false);
      })
    );
  }
  
  isAdmin() {
    return this.userClaims?.admin === true;
  }

  isUser() {
    return this.userClaims?.admin !== false;
  }

  get isLoggedIn(): boolean {
    const isNotLoginPage = window.location.pathname != '/login' && window.location.pathname != '/forgot-password';
    return this.auth.currentUser && isNotLoginPage ? true : false;
  }

  isUserVerified(): Promise<boolean> {
    return new Promise((resolve) => {
      onAuthStateChanged(this.auth, (user: User | null) => {
        resolve(!!user);
      });
    });
  }

  // google logins
  async signIn(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      this.loggedIn = true;
      return userCredential;
    } catch (error:any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        return error;
      } else {
        console.error('Error signing in:', error);
        throw error;
      }
    }
  }

  async sendOtp(error:any) {
    this.initializeRecaptcha();
    if (error.code === 'auth/multi-factor-auth-required' && this.recaptchaVerifier) {
      const resolver = getMultiFactorResolver(this.auth, error);
      const phoneInfoOptions = resolver.hints;
      const phoneAuthProvider = new PhoneAuthProvider(this.auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber({
        multiFactorHint: phoneInfoOptions[0],
        session: resolver.session
      }, this.recaptchaVerifier);
      return verificationId;
    } else {
      console.log(error.code);
      throw error;
    }
  }

  async verifyOtp(error:any, verificationId: string, code: string) {
    const resolver = getMultiFactorResolver(this.auth, error);
    const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, code);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
    return resolver.resolveSignIn(multiFactorAssertion);
  }

  async signOutUser() {
    await signOut(this.auth);
    this.localStorageService.clear();
    this.dataService.clear();
    this.loggedIn = false;
    this.isLoggedInAfterMFA = false;
    window.open('/login', '_self');
  }
  
  async resetPassword(email: string) {
    await sendPasswordResetEmail(this.auth, email);
  }

  get getUserDetails() {
    return this.auth.currentUser || null;
  }

  get getUserDetailsFromDB() {
    return this.localStorageService.get('userDetails') || { firstName: '', lastName: ''};
  }
  
  get getFullName() {
    return this.getUserDetailsFromDB.firstName + ' ' + this.getUserDetailsFromDB.lastName;
  }

  async setMFA(phoneNumber: string) {
    this.initializeRecaptcha();
    if (this.auth.currentUser) {
      try {
        const multiFactorSession = await multiFactor(this.auth.currentUser).getSession();
        const phoneInfoOptions = {
            phoneNumber: phoneNumber,
            session: multiFactorSession
        };        
        const phoneAuthProvider = new PhoneAuthProvider(this.auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier);
        return verificationId;        
      } catch (error) {
        console.log(error);
        throw error;
      }
    }
  }

  initializeRecaptcha() {
    if (!this.recaptchaVerifier) {
      this.recaptchaVerifier = new RecaptchaVerifier(this.auth, 'recaptcha-container-id', {
        size: 'invisible'
      });
    }
  }
  
  async enrollMFA(verificationId: string, code:string) {
    const cred = PhoneAuthProvider.credential(verificationId, code);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    if (this.auth.currentUser) {
      return multiFactor(this.auth.currentUser).enroll(multiFactorAssertion, this.auth.currentUser.email);
    }
  }
  
  // changes for inactivity of user
  private listenForUserActivity(): void {
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];

    activityEvents.forEach(event => {
      document.addEventListener(event, () => this.resetInactivityTimer());
    });

    this.userActivitySubscription.add(
      new Subscription(() => {
        activityEvents.forEach(event => {
          document.removeEventListener(event, () => this.resetInactivityTimer());
        });
      })
    );
  }

  private resetInactivityTimer(): void {
    clearTimeout(this.inactivityTimeout);

    this.inactivityTimeout = setTimeout(() => {      
      this.logoutUser();
    }, 900000);
  }

  public async logoutUser(): Promise<void> {
    try {
      await this.signOutUser();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  ngOnDestroy(): void {
    this.userActivitySubscription.unsubscribe();
    clearTimeout(this.inactivityTimeout);
  }

}